import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ChevronDown, 
  ChevronUp, 
  ArrowLeft, 
  Camera, 
  Music, 
  PartyPopper, 
  CheckCircle, 
  AlertCircle, 
  DollarSign,
  Users,
  ClipboardList,
  Lightbulb,
  MessageCircle
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const VendorGuidePage = () => {
  const navigate = useNavigate();
  const [expandedGuides, setExpandedGuides] = useState({});

  const toggleGuide = (guideId) => {
    setExpandedGuides(prev => ({
      ...prev,
      [guideId]: !prev[guideId]
    }));
  };

  // Service-specific guides data
  const serviceGuides = [
    {
      id: 'photographers',
      title: 'Photographers',
      icon: Camera,
      color: 'blue',
      customerProvides: [
        'Event date, time, and location details',
        'List of must-have shots (family groups, specific moments)',
        'Access to all areas where photos will be taken',
        'Contact info for key people (wedding planner, host, etc.)',
        'Timeline of key moments (first dance, cake cutting, speeches)'
      ],
      vendorProvides: [
        'Professional photography equipment',
        'Backup equipment in case of failure',
        'Photo editing and post-processing',
        'Delivery method (online gallery, USB, prints)',
        'Agreed-upon number of final edited images'
      ],
      includedInPrice: [
        'Hours of coverage as agreed',
        'Travel within service area',
        'Basic editing and color correction',
        'Online gallery for viewing/downloading',
        'Specified number of edited photos'
      ],
      additionalCharges: [
        'Extra hours beyond package (state your hourly rate)',
        'Travel outside service area (state mileage rate)',
        'Rush delivery (faster turnaround)',
        'Prints, albums, or physical products',
        'Second photographer if requested',
        'Drone/aerial photography'
      ],
      tips: [
        'Always have a shot list discussion before the event',
        'Send a timeline reminder 1-2 days before',
        'Bring business cards to give to guests',
        'Set clear expectations about editing style'
      ]
    },
    {
      id: 'djs',
      title: 'DJs / Entertainment',
      icon: Music,
      color: 'purple',
      customerProvides: [
        'Event date, time, and venue details',
        'Access to power outlets and setup area',
        'Music preferences and must-play songs',
        'Do-not-play list (if any)',
        'Timeline for announcements and special dances'
      ],
      vendorProvides: [
        'Sound system and speakers',
        'DJ equipment (mixer, controller, laptop)',
        'Microphone for announcements',
        'Music library and playlists',
        'Setup and breakdown'
      ],
      includedInPrice: [
        'Hours of music/entertainment as agreed',
        'Sound equipment for venue size',
        'Basic lighting (if included in package)',
        'Setup and breakdown time',
        'One planning meeting or call'
      ],
      additionalCharges: [
        'Extra hours beyond package',
        'Additional equipment (subwoofer, extra speakers)',
        'Special lighting effects (uplighting, dance floor lights)',
        'Fog/haze machine',
        'Emcee services (if not included)',
        'Travel outside service area'
      ],
      tips: [
        'Get the venue layout ahead of time',
        'Confirm power requirements with venue',
        'Have a backup playlist ready',
        'Do a sound check before guests arrive'
      ]
    },
    {
      id: 'party-decor',
      title: 'Party Decor / Setups',
      icon: PartyPopper,
      color: 'pink',
      customerProvides: [
        'Event date, time, and venue address',
        'Access to venue for setup (time window)',
        'Theme, color scheme, and style preferences',
        'Guest count (for table settings, etc.)',
        'Any venue restrictions on decorations'
      ],
      vendorProvides: [
        'Decorations and materials as agreed',
        'Setup labor and time',
        'Design consultation',
        'Coordination with venue if needed',
        'Breakdown and removal (if included)'
      ],
      includedInPrice: [
        'Decor items as specified in proposal',
        'Setup labor',
        'Design consultation or planning',
        'Travel within service area',
        'Basic supplies (tape, wire, etc.)'
      ],
      additionalCharges: [
        'Additional decor items not in original quote',
        'Last-minute changes or additions',
        'Breakdown/removal if not included',
        'Travel outside service area',
        'Rush orders (less than 2 weeks notice)',
        'Rental items (linens, chairs, arches)'
      ],
      tips: [
        'Visit the venue beforehand if possible',
        'Get written approval for any wall/ceiling attachments',
        'Take "before" photos of setup',
        'Confirm breakdown time with venue'
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: 'bg-blue-100 text-blue-600',
        button: 'border-blue-400 text-blue-700 hover:bg-blue-100'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-800',
        icon: 'bg-purple-100 text-purple-600',
        button: 'border-purple-400 text-purple-700 hover:bg-purple-100'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        text: 'text-pink-800',
        icon: 'bg-pink-100 text-pink-600',
        button: 'border-pink-400 text-pink-700 hover:bg-pink-100'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen-safe bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-4 text-gray-600 hover:text-gray-900"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Page Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <ClipboardList className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Service Provider Guide & Event Checklist</h1>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            Clear guidance to help you manage expectations, avoid misunderstandings, and deliver great service
          </p>
        </div>

        {/* General Guidelines Section */}
        <div className="space-y-6 mb-10">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            General Guidelines for All Services
          </h2>

          {/* What Customer Provides vs Vendor Provides */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="border-l-4 border-l-emerald-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-emerald-600" />
                  What the Customer Typically Provides
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600">
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Event date, time, and location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Access to venue for setup/breakdown</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Clear communication about needs and preferences</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Timely responses and approvals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Payment as agreed (deposit + final)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-blue-600" />
                  What You (the Service Provider) Provide
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600">
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Professional service as described</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>All equipment and materials needed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Clear communication and professionalism</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>On-time arrival and punctuality</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Backup plan for emergencies</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Guidelines */}
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                <DollarSign className="w-4 h-4 text-amber-600" />
                Pricing Best Practices
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-amber-900 space-y-3">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="font-semibold mb-2">What to Include in Your Price:</p>
                  <ul className="space-y-1 text-amber-800">
                    <li>• Hours of service covered</li>
                    <li>• Travel within your service area</li>
                    <li>• Equipment you&apos;ll bring</li>
                    <li>• Any deliverables (photos, etc.)</li>
                    <li>• Setup and breakdown time</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold mb-2">Be Clear About Extra Charges:</p>
                  <ul className="space-y-1 text-amber-800">
                    <li>• Overtime hourly rate</li>
                    <li>• Travel fees outside service area</li>
                    <li>• Additional equipment/services</li>
                    <li>• Rush or last-minute requests</li>
                    <li>• Cancellation/reschedule fees</li>
                  </ul>
                </div>
              </div>
              <div className="pt-2 border-t border-amber-200">
                <p className="text-xs text-amber-700">
                  <strong>Pro Tip:</strong> Always discuss potential additional charges upfront in your proposal. 
                  Customers appreciate transparency and it prevents misunderstandings later.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Communication Tips */}
          <Card className="border-gray-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-gray-600" />
                Communication Checklist
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-600">
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="font-semibold text-gray-800 mb-2">Before Booking:</p>
                  <ul className="space-y-1">
                    <li>☐ Discuss all requirements</li>
                    <li>☐ Provide clear quote</li>
                    <li>☐ Explain what&apos;s included</li>
                    <li>☐ Mention potential extras</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">After Booking:</p>
                  <ul className="space-y-1">
                    <li>☐ Confirm all details</li>
                    <li>☐ Send reminder 1 week out</li>
                    <li>☐ Confirm day-of contact</li>
                    <li>☐ Share your arrival time</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 mb-2">After Event:</p>
                  <ul className="space-y-1">
                    <li>☐ Thank the customer</li>
                    <li>☐ Deliver as promised</li>
                    <li>☐ Request a review</li>
                    <li>☐ Address any concerns</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Service-Specific Guides */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-amber-500" />
            Service-Specific Quick Guides
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            Click on your service type for detailed checklists tailored to your category
          </p>

          {serviceGuides.map((guide) => {
            const colors = getColorClasses(guide.color);
            const isExpanded = expandedGuides[guide.id];
            const IconComponent = guide.icon;

            return (
              <Card key={guide.id} className={`${colors.border} overflow-hidden`}>
                <button
                  onClick={() => toggleGuide(guide.id)}
                  className={`w-full text-left px-4 py-4 flex items-center justify-between ${colors.bg} hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colors.icon}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className={`font-semibold ${colors.text}`}>{guide.title}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className={`w-5 h-5 ${colors.text}`} />
                  ) : (
                    <ChevronDown className={`w-5 h-5 ${colors.text}`} />
                  )}
                </button>

                {isExpanded && (
                  <CardContent className="pt-4 space-y-4">
                    {/* Customer Provides */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-600" />
                        What the Customer Provides
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1 ml-6">
                        {guide.customerProvides.map((item, idx) => (
                          <li key={idx} className="list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Vendor Provides */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-blue-600" />
                        What You Provide
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1 ml-6">
                        {guide.vendorProvides.map((item, idx) => (
                          <li key={idx} className="list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Included in Price */}
                    <div className="bg-emerald-50 rounded-lg p-3">
                      <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        Typically Included in Price
                      </h4>
                      <ul className="text-sm text-emerald-700 space-y-1 ml-6">
                        {guide.includedInPrice.map((item, idx) => (
                          <li key={idx} className="list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Additional Charges */}
                    <div className="bg-amber-50 rounded-lg p-3">
                      <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        Common Additional Charges
                      </h4>
                      <ul className="text-sm text-amber-700 space-y-1 ml-6">
                        {guide.additionalCharges.map((item, idx) => (
                          <li key={idx} className="list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Pro Tips */}
                    <div className="bg-blue-50 rounded-lg p-3">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-blue-600" />
                        Pro Tips
                      </h4>
                      <ul className="text-sm text-blue-700 space-y-1 ml-6">
                        {guide.tips.map((item, idx) => (
                          <li key={idx} className="list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* More Categories Coming Soon */}
          <Card className="border-dashed border-gray-300 bg-gray-50">
            <CardContent className="py-6 text-center">
              <p className="text-gray-500">
                More service category guides coming soon!
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Caterers, Florists, Event Planners, and more...
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="py-6">
              <p className="font-semibold text-emerald-800 mb-3">Ready to get started?</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => navigate('/vendor/dashboard')}
                >
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  onClick={() => navigate('/vendor/dashboard?tab=profile')}
                >
                  Update My Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VendorGuidePage;
